import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useParams, useSearchParams } from "react-router";

import * as api from "../../lib/api.js";
import type { SessionResultOut } from "../contract/api";
import { t } from "../contract/strings";
import { RESULT_POLL_MS, RESULT_POLL_STEADY_ATTEMPTS } from "../result-poll";
import { summaryPendingOf, useApp } from "../state/store";
import { Button, Row, ScreenHeading, SkeletonGate, StatusLine, cx } from "../ui";
import { MeetingHeading } from "./meeting-heading";
import { DetailedTab, NotesSkeleton, viewFromParam, type NotesView } from "./result-detailed";
import { DetailsCard } from "./result-details";
import {
  LABEL,
  resultLoadProblemKey,
  tabFromParam,
  type ResultTab,
} from "./result-model";
import { useActionTicks } from "./result-actions-state";
import { AskAi } from "./result-chat";
import { chatOffered } from "./result-chat-model";
import { useMeetingChat } from "./result-chat-state";
import { useMeetingNotes } from "./result-notes-state";
import { QuickTab } from "./result-quick";
import { ResultTabBar, panelId, tabId } from "./result-tabs";
import { captureAnalytics } from "../analytics";
import { TranscriptTab, type JumpRequest } from "./result-transcript";
import { UsageAsk, useUsageAsk } from "./result-usage-ask";
import { Screen } from "./screen";

/**
 * `SessionResultOut` above is the server's own wire shape (`meetfolks/protocol.py` via
 * `contract/openapi.json`), not a hand-kept mirror: the fields, their optionality and the
 * `summary_status` markers are the model the server serialises. The comments that used to
 * sit on the removed interfaces are in that model, including #465's `capture_gaps`.
 *
 * The rows each tab reads are in `result-model.ts`; they are re-exported here for the
 * shared fixtures' test and anything else that read them from this module.
 */
export {
  cardSharePct,
  fmtMinSec,
  resultCommitments,
  resultLoadProblemKey,
  resultTopics,
  speechSecOf,
  summaryBody,
  summaryFailure,
  summaryStatusMessage,
} from "./result-model";

/**
 * Whether the page has scrolled past `sentinel` -- the top of the sticky details card --
 * read from an IntersectionObserver rather than a scroll listener. The sentinel sits
 * above the sticky block, so the card folding does not move it and cannot flicker.
 */
function useScrolledPast(
  sentinel: RefObject<HTMLElement | null>,
  active: boolean,
  onCross?: () => void,
): boolean {
  const [past, setPast] = useState(false);
  const crossed = useRef(onCross);
  useEffect(() => {
    crossed.current = onCross;
  });
  useEffect(() => {
    const el = sentinel.current;
    if (!active || !el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      setPast(!entry.isIntersecting && (entry.boundingClientRect?.top ?? 0) < 0);
      crossed.current?.();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [sentinel, active]);
  return past;
}

/**
 * The meeting notes, as three tabs over one result (#764): Quick (the summary, topics and
 * every next step), Detailed (one card per person) and Transcript (every turn, searchable).
 *
 * Two ways in, and with real history neither needs recording: a session that has just ended
 * navigates here from the engine, and the list under Account deep-links by id. The result
 * comes from the store when the engine has it -- it keeps polling for the summary after the
 * transcript publishes, so the page fills in without being asked -- and from a fetch when
 * this is a deep link. The open tab is in the address (`?tab=`), so Back, a reload and a
 * shared link keep it.
 */
export function Result({
  forceUsageAsk = false,
}: {
  /** The parity gallery's `usage_ask` state: the one-time ask, whatever is stored (#1259). */
  forceUsageAsk?: boolean;
} = {}) {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const stored = useApp((s) => s.result);
  const storedId = useApp((s) => s.resultSessionId);
  const identityLoaded = useApp((s) => s.identityLoaded);
  const claimProfileId = useApp((s) => s.credentials?.claim_profile_id ?? null);
  const live = storedId === id ? (stored as SessionResultOut | null) : null;

  const [fetched, setFetched] = useState<SessionResultOut | null>(null);
  const [problem, setProblem] = useState<{
    message: string;
    kind: "warn" | "err";
  } | null>(null);
  /** Processing failed on the server: terminal until somebody retries. */
  const [failed, setFailed] = useState(false);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [isCreator, setIsCreator] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [jump, setJump] = useState<JumpRequest | null>(null);
  const jumps = useRef(0);
  const detailsSentinel = useRef<HTMLDivElement | null>(null);
  const [foldHeight, setFoldHeight] = useState(0);

  const tab = tabFromParam(searchParams.get("tab"));
  // The notes in a style (#774): loaded when Detailed opens, kept here for Export.
  const notes = useMeetingNotes(id ?? "", tab === "detailed" && Boolean(id) && identityLoaded);
  const noteStyle = searchParams.get("style");
  const noteView = viewFromParam(searchParams.get("view"));

  /*
   * [V] Held until boot has handed the API its token, which is the one thing this screen
   * cannot assume.
   *
   * `/session/:id/result` is the link in the account history and it must survive a cold
   * load. It once had no `Guard`, so nothing waited for boot: on a cold load this effect
   * ran on the first render, before
   * `api.setToken(credentials.token)`, so the request went out unauthenticated and came
   * back 401. The deps were `[id, live]`, neither of which changes when boot finishes, so
   * the effect never ran again and the screen showed "not readable by this device" for a
   * transcript the device recorded. Warm navigation from the session hid it completely:
   * `live` is already in the store, so the fetch never happens.
   */
  useEffect(() => {
    if (live || !id || !identityLoaded) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const poll = async () => {
      try {
        const out = await api.getResult(id);
        if (!alive) return;
        if (out.ready) {
          setFetched(out.result);
          setProblem(null);
          setFailed(false);
          if (!summaryPendingOf(out.result)) return;
        } else if (out.degraded || out.session_state === "failed") {
          // Terminal: the server gave up on this session. Absent while processing is
          // still in flight, so a slow run never reads as a failure.
          setFailed(true);
          setProblem(null);
          return;
        } else {
          // A 202 is not an error: the session exists and is still being processed. Its
          // `processing_stage` is a pipeline internal and is not shown.
          setProblem(null);
        }
        attempts++;
        if (attempts < RESULT_POLL_STEADY_ATTEMPTS) {
          timer = setTimeout(poll, RESULT_POLL_MS);
        } else {
          setPollExhausted(true);
          setProblem({
            message: t("result.waiting_long"),
            kind: "warn",
          });
        }
      } catch (err) {
        // The most likely refusal is the deliberate one: a transcript is only readable by a
        // device that took part, and that rule is not relaxed by owning the account.
        // The server's own words are for the operator, so a reader gets plain copy.
        if (alive) {
          const status = (err as { status?: number }).status;
          setProblem({ message: t(resultLoadProblemKey(status)), kind: "err" });
        }
      }
    };

    void poll();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [id, live, identityLoaded, retryNonce]);

  /*
   * Whether this device started the session, which is who may retry a failed one. Read
   * from the session itself, because this screen is often a deep link with no engine
   * state behind it. Asked only once the session is known to have failed; a refusal
   * leaves the retry button off rather than offering one the server will refuse.
   */
  useEffect(() => {
    if (!failed || !id || !claimProfileId) return;
    let alive = true;
    api.getSession(id).then(
      (session: { creator_claim_profile_id?: string | null }) => {
        if (alive) setIsCreator(session?.creator_claim_profile_id === claimProfileId);
      },
      () => {
        if (alive) setIsCreator(false);
      }
    );
    return () => {
      alive = false;
    };
  }, [failed, id, claimProfileId]);

  function retryResult(): void {
    captureAnalytics("retry_tapped", { surface: "result_load" });
    setFetched(null);
    setProblem(null);
    setFailed(false);
    setPollExhausted(false);
    setRetryNonce((nonce) => nonce + 1);
  }

  async function retryProcessing(): Promise<void> {
    if (!id) return;
    captureAnalytics("retry_tapped", { surface: "result_processing" });
    setRetrying(true);
    try {
      await api.retryProcessing(id);
      retryResult();
    } catch (err) {
      const status = (err as { status?: number }).status;
      setProblem({
        message:
          status === 404 || status === 405
            ? t("result.retry_unavailable")
            : t("result.retry_failed"),
        kind: "err",
      });
    } finally {
      setRetrying(false);
    }
  }

  const result = live ?? fetched;
  /*
   * A jump folds the card before it scrolls, so the scroll is worked out for the folded
   * header and the turn is not left under it. The pin lasts until the card's place next
   * crosses the top of the screen; from then on the scroll position decides again.
   */
  const [foldPinned, setFoldPinned] = useState(false);
  const scrolledPast = useScrolledPast(detailsSentinel, result !== null, () => setFoldPinned(false));
  const detailsCollapsed = scrolledPast || foldPinned;

  /**
   * A rename the server took (#708): every copy of this result takes the new name, so
   * coming back to this screen from the store shows it rather than the old one.
   */
  function keepRename(title: string): void {
    setFetched((prev) => (prev ? { ...prev, title } : prev));
    const app = useApp.getState();
    if (app.resultSessionId === id && app.result) {
      useApp.setState({ result: { ...(app.result as SessionResultOut), title } });
    }
  }

  function keepSpeakerResult(next: SessionResultOut): void {
    setFetched(next);
    const app = useApp.getState?.();
    if (app?.resultSessionId === id) useApp.setState?.({ result: next });
  }

  /**
   * Open a tab. The address is replaced rather than pushed, so Back leaves the notes
   * instead of stepping back through the tabs, and the tab is still there to come back to.
   */
  function openTab(next: ResultTab): void {
    // Pressing the open tab changes nothing, a jump in progress (and its tint) included.
    if (next === tab) return;
    captureAnalytics("result_tab_selected", { tab: next });
    setJump(null);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "quick") params.delete("tab");
        else params.set("tab", next);
        return params;
      },
      { replace: true },
    );
  }

  /** Detailed's open style and view (#774), kept in the address like the tab. */
  function setNotesParam(name: "style" | "view", value: string | null): void {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value === null) params.delete(name);
        else params.set(name, value);
        return params;
      },
      { replace: true },
    );
  }

  /** "Jump to this moment": open the transcript at the turn that covers `time`. */
  function jumpTo(time: number): void {
    openTab("transcript");
    setFoldPinned(true);
    jumps.current += 1;
    setJump({ time, nonce: jumps.current });
  }

  // One speaker, one hue: the cards' order, which the transcript's turns take too.
  const speakerOrder = useMemo(() => (result?.cards ?? []).map((c) => c.speaker), [result]);
  // The meeting's shared action ticks (#786): one state for Quick and Detailed, read once
  // the result is here and the API has its token.
  const ticks = useActionTicks(result, Boolean(result) && identityLoaded);
  // Ask AI (#791): this person's own chat, read once the result is here so the screen
  // knows whether to offer it. The panel's open state is the screen's; its chat is too,
  // so closing the panel loses nothing and an answer keeps arriving while it is closed.
  const chat = useMeetingChat(id ?? "", Boolean(result) && identityLoaded);
  const [chatOpen, setChatOpen] = useState(false);
  const chatButton = useRef<HTMLButtonElement | null>(null);
  const chatWasOpen = useRef(false);
  function toggleChat(): void {
    const next = !chatOpen;
    if (next) {
      captureAnalytics("ask_ai_opened", {});
      captureAnalytics("dialog_viewed", { dialog: "ask_ai" });
    } else {
      captureAnalytics("dialog_dismissed", { dialog: "ask_ai" });
    }
    setChatOpen(next);
  }
  function closeChat(): void {
    captureAnalytics("dialog_dismissed", { dialog: "ask_ai" });
    setChatOpen(false);
  }
  // Another meeting opens with the chat closed.
  useEffect(() => {
    chatWasOpen.current = false;
    setChatOpen(false);
  }, [id]);
  useEffect(() => {
    // Focus goes back to the button the panel was opened from.
    if (chatWasOpen.current && !chatOpen) chatButton.current?.focus({ preventScroll: true });
    chatWasOpen.current = chatOpen;
  }, [chatOpen]);
  const offerChat = chatOffered(chat.thread);
  // The one-time usage-stats ask (#1259), once the notes are on screen.
  const usageAsk = useUsageAsk(Boolean(result), forceUsageAsk);

  if (!result) {
    if (failed) {
      // Plain words, no codes: the reader cannot act on a pipeline marker. The creator
      // is the one person who can start it again.
      return (
        <Screen name="result">
          <ScreenHeading>{t("result.heading")}</ScreenHeading>
          <section
            role="status"
            aria-label={t("result.failed_aria")}
            className="p-4 bg-surface border border-danger/40 rounded-card flex flex-col gap-stack"
          >
            <p className="m-0 text-note text-ink">
              {isCreator
                ? t("result.failed_creator")
                : t("result.failed_guest")}
            </p>
          </section>
          {problem ? <StatusLine kind={problem.kind}>{problem.message}</StatusLine> : null}
          {isCreator ? (
            <Row>
              <Button disabled={retrying} onClick={() => void retryProcessing()}>
                {t("result.try_again")}
              </Button>
            </Row>
          ) : null}
        </Screen>
      );
    }
    return (
      <Screen name="result">
        <ScreenHeading>{t("result.heading")}</ScreenHeading>
        {problem?.kind === "err" ? (
          <StatusLine kind="err">{problem.message}</StatusLine>
        ) : (
          <section
            aria-live="polite"
            aria-label={t("result.processing_aria")}
            className="p-4 bg-surface border border-line rounded-card flex flex-col gap-stack"
          >
            <span className="font-title text-person font-semibold tracking-headline text-ink">
              {t("result.preparing")}
            </span>
            <p className="m-0 text-note text-ink">
              {pollExhausted && problem
                ? problem.message
                : t("result.leave_screen")}
            </p>
          </section>
        )}
        {pollExhausted ? (
          <Row>
            <Button variant="ghost" onClick={retryResult}>
              {t("result.check_again")}
            </Button>
          </Row>
        ) : null}
      </Screen>
    );
  }

  return (
    <Screen name="result">
      {/* No scroll anchoring here: when the details card folds, the browser would move
          the scroll by the height it lost, bring the card's place back into view and
          unfold it again, over and over. */}
      {/* The tabs sit close over their content: whatever follows the sticky block (the
          partial banner or the panel) cancels the column's gap, leaving only the block's
          own bottom padding between the tab bar and it. */}
      <div
        className={cx(
          "flex flex-col w-full gap-4 [overflow-anchor:none] [&>[data-result-sticky]+*]:-mt-4",
          // Room under the last card for the floating Ask AI button, so it never covers it.
          // The usage-stats ask (#1259) takes the pill's room while it is up.
          offerChat || usageAsk.shown ? "pb-24" : "pb-8",
        )}
      >
        {/* Keyed by meeting: opening another one from here (a notification, Back and
            forward) starts with its own name, never this one's saved name or open field. */}
        <MeetingHeading
          key={result.session_id}
          sessionId={result.session_id}
          title={result.title}
          createdAt={result.created_at}
          canRename={result.can_rename === true}
          onRenamed={keepRename}
        />

        {/* The details card and the tab bar stick to the top together; past the card's own
            place the card folds to its first row, which reads the meeting's name. */}
        <div ref={detailsSentinel} className="h-0" aria-hidden="true" data-details-sentinel />
        <div
          data-result-sticky
          // Bled out to the plate's edges (`-mx-pad px-pad`) so the pinned row's band spans
          // the whole width while the card and the tabs stay in the reading column.
          // No section gap of its own: the band under the card carries the space down to
          // the tab bar, so no strip of the notes ever shows between them.
          className="sticky top-0 z-20 -mt-4 -mx-pad px-pad flex flex-col"
        >
          {/* The pinned row's band. While folded it is the page tone over the notes blurred
              behind it (`backdrop-blur-[10px]`, and a near-opaque plate where
              the browser has no backdrop-filter, as the chat's backdrop does), with the small
              box carrying the name centred in it. Unfolded the plate is not painted: the card
              is in the page. */}
          <div className="relative flex items-center justify-center py-3">
            <div
              aria-hidden="true"
              data-details-frost
              className={cx(
                "pointer-events-none absolute inset-0 bg-bg/70 backdrop-blur-[10px]",
                "supports-[not((backdrop-filter:blur(1px))_or_(-webkit-backdrop-filter:blur(1px)))]:bg-bg/95",
                "transition-opacity duration-200 ease-out motion-reduce:transition-none",
                detailsCollapsed ? "opacity-100" : "opacity-0",
              )}
            />
            <div className="relative w-full">
              <DetailsCard result={result} collapsed={detailsCollapsed} onFoldHeight={setFoldHeight} />
            </div>
          </div>
          <div className="py-2 bg-bg">
            <ResultTabBar value={tab} onChange={openTab} />
          </div>
        </div>

        {result.state === "partial" ? (
          <section
            role="status"
            aria-label={t("result.partial_aria")}
            className="p-4 bg-surface-3 border border-warn-ink/40 rounded-card"
          >
            <strong className={cx(LABEL, "text-label text-warn-ink")}>
              {t("result.partial")}
            </strong>
            <p className="m-0 mt-2 text-note text-ink">
              {t("result.partial_note")}
            </p>
          </section>
        ) : null}

        <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
          {tab === "quick" ? (
            <QuickTab result={result} ticks={ticks} onJump={jumpTo} onSpeakerRenamed={keepSpeakerResult} />
          ) : tab === "detailed" ? (
            // The styles' first read is a skeleton (#1043); a failure is the tab's own card.
            <SkeletonGate
              loading={!notes.data && notes.problem !== "load_failed"}
              label={t("result.notes_loading")}
              skeleton={<NotesSkeleton />}
            >
              <DetailedTab
                result={result}
                notes={notes}
                ticks={ticks}
                style={noteStyle}
                view={noteView}
                onStyle={(style) => setNotesParam("style", style)}
                onView={(view: NotesView) => setNotesParam("view", view === "map" ? "map" : null)}
                onJump={jumpTo}
              />
            </SkeletonGate>
          ) : (
            <TranscriptTab
              result={result}
              speakerOrder={speakerOrder}
              jump={jump}
              loadNotes={notes.ensure}
            />
          )}
        </div>

        {/* While the card is folded, the room its topics gave up, so a short page keeps
            its height and the fold cannot pull the scroll back and undo itself. */}
        {detailsCollapsed && foldHeight > 0 ? (
          <div aria-hidden="true" data-fold-spacer style={{ height: `${foldHeight}px` }} />
        ) : null}
      </div>

      {usageAsk.shown ? <UsageAsk onAnswer={usageAsk.answer} /> : null}
      {/* Ask AI's pill shares the bottom corner with the ask, so it waits for the answer. */}
      {offerChat && !usageAsk.shown ? (
        <AskAi
          // Keyed by meeting, like the heading: another meeting opened from here starts
          // with its own empty box and its own suggestions, never this one's draft.
          key={result.session_id}
          chat={chat}
          open={chatOpen}
          onToggle={toggleChat}
          onClose={closeChat}
          buttonRef={chatButton}
          onJump={(time) => {
            // The chat is modal over a blurred page: close it, then take the usual jump.
            closeChat();
            jumpTo(time);
          }}
        />
      ) : null}
    </Screen>
  );
}
